/**
 * Trunnion AI - Schedule Builder Dialog
 * Visual cron/schedule builder rendered inside a Modal.
 * Supports Once, Hourly, Daily, Weekly, Monthly, and Custom frequencies.
 * Updates the workflow's triggerConfig when a schedule is saved.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { CalendarClock, Trash2 } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { CronExpression, describeCron, validateCron } from './CronExpression';
import { SchedulePreview } from './SchedulePreview';
import { useWorkflowDesignerStore } from '@/stores/workflowDesignerStore';

interface ScheduleBuilderProps {
  open: boolean;
  onClose: () => void;
}

type Frequency = 'once' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom';

const FREQUENCY_OPTIONS = [
  { value: 'once', label: 'Once' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom (Cron)' },
];

const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`,
}));

const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => ({
  value: String(i),
  label: String(i).padStart(2, '0'),
}));

const DAY_OF_MONTH_OPTIONS = Array.from({ length: 31 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

function buildCronFromState(
  frequency: Frequency,
  hour: string,
  minute: string,
  selectedDays: number[],
  dayOfMonth: string,
  customCron: string,
): string {
  switch (frequency) {
    case 'once':
      // "Once" doesn't produce a valid repeating cron; store specific time
      return `${minute} ${hour} * * *`;
    case 'hourly':
      return `${minute} * * * *`;
    case 'daily':
      return `${minute} ${hour} * * *`;
    case 'weekly': {
      const days = selectedDays.length > 0 ? selectedDays.sort().join(',') : '*';
      return `${minute} ${hour} * * ${days}`;
    }
    case 'monthly':
      return `${minute} ${hour} ${dayOfMonth} * *`;
    case 'custom':
      return customCron;
    default:
      return '0 0 * * *';
  }
}

export function ScheduleBuilder({ open, onClose }: ScheduleBuilderProps) {
  const { nodes, updateNodeData } = useWorkflowDesignerStore();

  // Find the trigger node to read existing schedule
  const triggerNode = useMemo(
    () => nodes.find((n) => n.data.nodeType === 'trigger'),
    [nodes],
  );

  const existingSchedule = triggerNode?.data.nodeType === 'trigger'
    ? triggerNode.data.schedule
    : undefined;

  // State
  const [frequency, setFrequency] = useState<Frequency>('daily');
  const [hour, setHour] = useState('9');
  const [minute, setMinute] = useState('0');
  const [selectedDays, setSelectedDays] = useState<number[]>([1, 2, 3, 4, 5]); // Mon-Fri
  const [dayOfMonth, setDayOfMonth] = useState('1');
  const [timezone, setTimezone] = useState('UTC');
  const [customCron, setCustomCron] = useState('0 9 * * *');
  const [onceDate, setOnceDate] = useState('');
  const [onceTime, setOnceTime] = useState('09:00');

  // Initialize from existing trigger schedule
  useEffect(() => {
    if (!existingSchedule) return;
    setCustomCron(existingSchedule);
    // Try to parse the existing cron into visual fields
    parseCronIntoState(existingSchedule);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingSchedule]);

  const parseCronIntoState = useCallback((cron: string) => {
    if (!cron) return;
    const parts = cron.trim().split(/\s+/);
    if (parts.length !== 5) {
      setFrequency('custom');
      return;
    }

    const [min, hr, dom, mon, dow] = parts;

    // Hourly
    if (min !== '*' && hr === '*' && dom === '*' && mon === '*' && dow === '*') {
      setFrequency('hourly');
      setMinute(min);
      return;
    }

    // Weekly
    if (min !== '*' && hr !== '*' && dom === '*' && mon === '*' && dow !== '*') {
      setFrequency('weekly');
      setMinute(min);
      setHour(hr);
      setSelectedDays(dow.split(',').map(Number));
      return;
    }

    // Monthly
    if (min !== '*' && hr !== '*' && dom !== '*' && mon === '*' && dow === '*') {
      setFrequency('monthly');
      setMinute(min);
      setHour(hr);
      setDayOfMonth(dom);
      return;
    }

    // Daily
    if (min !== '*' && hr !== '*' && dom === '*' && mon === '*' && dow === '*') {
      setFrequency('daily');
      setMinute(min);
      setHour(hr);
      return;
    }

    // Fallback to custom
    setFrequency('custom');
  }, []);

  const cronExpression = useMemo(
    () => buildCronFromState(frequency, hour, minute, selectedDays, dayOfMonth, customCron),
    [frequency, hour, minute, selectedDays, dayOfMonth, customCron],
  );

  const cronError = useMemo(() => validateCron(cronExpression), [cronExpression]);

  const toggleDay = (day: number) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    );
  };

  const handleSave = () => {
    if (triggerNode) {
      updateNodeData(triggerNode.id, {
        triggerType: frequency === 'once' ? 'manual' : 'scheduled',
        schedule: frequency === 'once' ? undefined : cronExpression,
      });
    }
    onClose();
  };

  const handleRemoveSchedule = () => {
    if (triggerNode) {
      updateNodeData(triggerNode.id, {
        triggerType: 'manual',
        schedule: undefined,
      });
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Workflow Schedule"
      description="Configure when this workflow should run automatically"
      size="xl"
      footer={
        <div className="flex w-full items-center justify-between">
          <Button
            variant="danger"
            size="sm"
            icon={<Trash2 className="h-4 w-4" />}
            onClick={handleRemoveSchedule}
          >
            Remove Schedule
          </Button>
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              icon={<CalendarClock className="h-4 w-4" />}
              onClick={handleSave}
              disabled={!!cronError && frequency !== 'once'}
            >
              Save Schedule
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Schedule status */}
        {existingSchedule && (
          <div className="flex items-center gap-2">
            <Badge variant="success" dot>
              Active Schedule
            </Badge>
            <span className="text-sm text-trunnion-slate-400">
              {describeCron(existingSchedule)}
            </span>
          </div>
        )}

        {/* Frequency selector */}
        <Select
          label="Frequency"
          options={FREQUENCY_OPTIONS}
          value={frequency}
          onChange={(e) => setFrequency(e.target.value as Frequency)}
        />

        {/* Once: date and time picker */}
        {frequency === 'once' && (
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Date"
              type="date"
              value={onceDate}
              onChange={(e) => setOnceDate(e.target.value)}
              className="[color-scheme:dark]"
            />
            <Input
              label="Time"
              type="time"
              value={onceTime}
              onChange={(e) => setOnceTime(e.target.value)}
              className="[color-scheme:dark]"
            />
          </div>
        )}

        {/* Hourly: minute selector */}
        {frequency === 'hourly' && (
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="At Minute"
              options={MINUTE_OPTIONS}
              value={minute}
              onChange={(e) => setMinute(e.target.value)}
            />
          </div>
        )}

        {/* Daily: hour and minute */}
        {frequency === 'daily' && (
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Hour"
              options={HOUR_OPTIONS}
              value={hour}
              onChange={(e) => setHour(e.target.value)}
            />
            <Select
              label="Minute"
              options={MINUTE_OPTIONS}
              value={minute}
              onChange={(e) => setMinute(e.target.value)}
            />
          </div>
        )}

        {/* Weekly: day checkboxes + time */}
        {frequency === 'weekly' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-trunnion-slate-200 mb-2 block">
                Days of Week
              </label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map((day) => (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => toggleDay(day.value)}
                    className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors border ${
                      selectedDays.includes(day.value)
                        ? 'bg-trunnion-gold-500/20 text-trunnion-gold-500 border-trunnion-gold-500/40'
                        : 'bg-trunnion-slate-800 text-trunnion-slate-400 border-trunnion-slate-600 hover:border-trunnion-slate-500 hover:text-trunnion-slate-300'
                    }`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Hour"
                options={HOUR_OPTIONS}
                value={hour}
                onChange={(e) => setHour(e.target.value)}
              />
              <Select
                label="Minute"
                options={MINUTE_OPTIONS}
                value={minute}
                onChange={(e) => setMinute(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Monthly: day of month + time */}
        {frequency === 'monthly' && (
          <div className="space-y-4">
            <Select
              label="Day of Month"
              options={DAY_OF_MONTH_OPTIONS}
              value={dayOfMonth}
              onChange={(e) => setDayOfMonth(e.target.value)}
            />
            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Hour"
                options={HOUR_OPTIONS}
                value={hour}
                onChange={(e) => setHour(e.target.value)}
              />
              <Select
                label="Minute"
                options={MINUTE_OPTIONS}
                value={minute}
                onChange={(e) => setMinute(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Custom: raw cron editor */}
        {frequency === 'custom' && (
          <CronExpression
            value={customCron}
            onChange={setCustomCron}
            showToggle={false}
          />
        )}

        {/* Timezone selector */}
        <Select
          label="Timezone"
          options={TIMEZONE_OPTIONS}
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
        />

        {/* Cron expression display (for non-custom modes) */}
        {frequency !== 'custom' && frequency !== 'once' && (
          <CronExpression
            value={cronExpression}
            onChange={() => {}}
            showToggle={true}
          />
        )}

        {/* Preview of upcoming runs */}
        {frequency !== 'once' && (
          <SchedulePreview
            cron={cronExpression}
            timezone={timezone}
            disabled={!!cronError}
          />
        )}

        {/* Once mode: single run info */}
        {frequency === 'once' && onceDate && onceTime && (
          <div className="rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-900/50 p-4">
            <div className="flex items-center gap-2 text-trunnion-slate-300">
              <CalendarClock className="h-4 w-4 text-trunnion-gold-500" />
              <span className="text-sm">
                Scheduled for {onceDate} at {onceTime}{' '}
                <span className="text-trunnion-slate-500">({timezone})</span>
              </span>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
